/**
 * Configurable QA rejection strikeout threshold.
 * Resolution: ticket.rejectionCapOverride.maxCount →
 * company.settings.maxRejectionCount → DEFAULT (3).
 * Bounds: 1..20.
 */

/** Default when unset / invalid. */
export const DEFAULT_MAX_REJECTION_COUNT = 3;
export const MIN_MAX_REJECTION_COUNT = 1;
export const MAX_MAX_REJECTION_COUNT = 20;

/**
 * @param {unknown} value
 * @returns {number}
 */
export function readRejectionCount(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

/**
 * Clamp a candidate max into [1, 20], or null if unset/invalid.
 * @param {unknown} value
 * @returns {number|null}
 */
export function clampMaxRejectionCount(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  const floored = Math.floor(n);
  if (floored < MIN_MAX_REJECTION_COUNT || floored > MAX_MAX_REJECTION_COUNT) {
    return null;
  }
  return floored;
}

/**
 * @param {object|null|undefined} ticket
 * @param {object|null|undefined} companySettings companies/{id}.settings
 * @returns {{ maxCount: number, source: 'ticket_override'|'workspace_default'|'global_default' }}
 */
export function resolveEffectiveMaxRejectionCount(ticket, companySettings) {
  const override =
    ticket &&
    ticket.rejectionCapOverride &&
    typeof ticket.rejectionCapOverride === 'object'
      ? ticket.rejectionCapOverride
      : null;
  if (override) {
    const fromTicket = clampMaxRejectionCount(override.maxCount);
    if (fromTicket != null) {
      return { maxCount: fromTicket, source: 'ticket_override' };
    }
  }

  const settings =
    companySettings && typeof companySettings === 'object'
      ? companySettings
      : {};
  const fromWorkspace = clampMaxRejectionCount(settings.maxRejectionCount);
  if (fromWorkspace != null) {
    return { maxCount: fromWorkspace, source: 'workspace_default' };
  }

  return {
    maxCount: DEFAULT_MAX_REJECTION_COUNT,
    source: 'global_default',
  };
}

/**
 * @param {number} rejectionCount
 * @param {number} maxCount
 * @returns {string}
 */
export function buildStrikeoutMessage(rejectionCount, maxCount) {
  const n = readRejectionCount(rejectionCount);
  const max =
    clampMaxRejectionCount(maxCount) ?? DEFAULT_MAX_REJECTION_COUNT;
  return (
    'Agent execution suspended. Max automated QA rejections ' +
    `reached (${n}/${max}). Human intervention required.`
  );
}
